import { Button } from './Button.js';

/**
 * The control that makes the rest of a paged list reachable.
 *
 * Written for the bookings screen (066, gap-list D5), promoted here when `MyReviewsPage` became
 * its second caller (H17). `GET /bookings` and `GET /reviews` answer in the same
 * `{items, total, page, pages}` envelope, and both screens used to read only `items` — so a
 * customer past the first page was shown a truncated list presented as a complete one, with no
 * control that could reach the rest.
 *
 * It renders no bookings and no reviews and knows about neither: the name `BookingsPager` was only
 * ever true of its location.
 *
 * Pure props in, DOM out: no hook, no fetch, no router. That is what lets its own suite mock
 * nothing at all, so its assertions cannot be blinded by the `useFetch` stub twelve suites carry.
 */
interface PagerProps {
  /** The page being shown. 1-based, as the platform counts. */
  page: number;
  /** How many pages exist, straight from the platform's answer — never computed here. */
  pages: number;
  onPage: (page: number) => void;
}

export function Pager({ page, pages, onPage }: PagerProps) {
  // Nothing to reach, so nothing to offer. `pages` is 0 for an empty list — `Math.ceil(0 / 20)`
  // — and 1 when everything fits on one page; in both cases a customer must see the screen
  // exactly as it was before this feature, not a pager with two dead controls (FR-004, SC-005).
  if (pages <= 1) return null;

  return (
    <div className="flex items-center justify-between gap-3 pt-4">
      {page > 1 ? (
        <Button variant="ghost" size="sm" onClick={() => onPage(page - 1)}>
          Previous
        </Button>
      ) : (
        // Holds the right-hand control in place on the first page, so Next does not jump
        // leftwards as the customer moves between pages.
        <span />
      )}
      {/* FR-012: rendered from `pages` as the platform just stated it. This component is only ever
          mounted on a successful read, so there is no remembered count that could survive a
          failure and be painted beside it as though the platform had said so. */}
      <span className="text-sm text-gray-400">
        Page {page} of {pages}
      </span>
      {page < pages ? (
        <Button variant="ghost" size="sm" onClick={() => onPage(page + 1)}>
          Next
        </Button>
      ) : (
        <span />
      )}
    </div>
  );
}

/**
 * The way out of a page that cannot be shown (066 FR-011, promoted in H17).
 *
 * Without it a customer who lands on a stale `?page=9` bookmark, or whose page 3 read fails, is
 * left on a screen with nothing that leads back to the records they still have. `ResultState`
 * offers an onward control only on `gone`, and widening it would put a pager's concern inside a
 * component twelve screens render — so this belongs beside the pager.
 *
 * Takes a callback rather than a path: each screen owns its own address.
 */
export function BackToFirstPage({ onFirstPage }: { onFirstPage: () => void }) {
  return (
    <Button variant="ghost" onClick={onFirstPage}>
      Back to first page
    </Button>
  );
}
